import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Eye, GitBranch } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CALCULATOR_LIST } from "../content/calculators";
import { useSeo } from "../lib/seo";

const FEATURES = [
  { icon: Zap, title: "Instant, no sign-up", body: "The calculator loads with the numbers already in it. Change anything and results update immediately — no account, no paywall, no ads in the way." },
  { icon: Eye, title: "Real profit, not price minus cost", body: "We include commission, shipping, packaging, ads, GST and returns — the costs that quietly turn a 'profitable' order into a loss." },
  { icon: GitBranch, title: "Scenario analysis", body: "Every calculator shows how profit changes as you move price or return rate, so you can price with your eyes open." },
  { icon: ShieldCheck, title: "No invented fees", body: "We never hard-code marketplace fees as facts. You enter your real numbers, and we label every assumption with a date." },
];

const HOME_FAQ = [
  { q: "Are the calculators really free?", a: "Yes. All calculators are free with no sign-up required. In future, a paid dashboard will let you track and compare your whole product catalogue — but the calculators stay free." },
  { q: "Do you store my numbers?", a: "Not unless you choose to save a calculation. We use privacy-conscious analytics that you can reject, and we never collect unnecessary personal data." },
  { q: "Are marketplace fees accurate?", a: "Marketplace fees change and vary by category, so we don't hard-code them. You enter your actual fees and we clearly label every default as an editable assumption." },
];

export default function Home() {
  useSeo({
    title: "ProfitMigo — Calculate Your Costs. Set Your Price. Know Your Profit.",
    description: "Free calculators for sellers, freelancers and small businesses to understand pricing, profit, fees and break-even points. Meesho, Amazon, COD/RTO and product profit calculators.",
    path: "/",
  });

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="max-w-3xl">
            <span className="inline-block rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              For e-commerce sellers & small businesses
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Calculate Your Costs. Set Your Price. Know Your Profit.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground sm:text-xl">
              Free calculators for sellers, freelancers and small businesses to understand pricing, profit, fees and break-even points.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" data-testid="hero-primary-cta">
                <Link to="/ecommerce/product-profit-calculator">
                  Open the profit calculator <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-secondary-cta">
                <Link to="/calculators">Browse all calculators</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Popular calculators */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Popular calculators</h2>
          <Link to="/calculators" className="text-sm font-medium text-primary hover:underline">View all</Link>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CALCULATOR_LIST.map((c, i) => (
            <Link
              key={c.slug}
              to={c.path}
              data-testid={`home-calc-${c.slug}`}
              className={`group flex flex-col justify-between rounded-xl border border-border bg-card p-6 transition-all hover:border-primary hover:shadow-sm ${i === 0 ? "lg:col-span-2" : ""}`}
            >
              <div>
                <h3 className="font-display text-xl font-semibold">{c.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.valueProp}</p>
              </div>
              <span className="mt-6 inline-flex items-center text-sm font-medium text-primary">
                Open calculator <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Why use us */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Why use our calculators?</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                  <f.icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <div>
                  <h3 className="font-semibold">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How calculations work */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">How calculations work</h2>
            <p className="mt-4 text-muted-foreground">
              Every calculator is powered by the same tested, transparent formula engine. We show you the methodology, the worked example and the assumptions — nothing is hidden.
            </p>
            <p className="mt-3 text-muted-foreground">
              The philosophy is simple: <span className="font-medium text-foreground">Calculate → Understand → Save → Track → Optimize</span>. Start with a single calculation today; track your whole catalogue's profitability tomorrow.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-slate-900 p-6 font-mono text-sm text-slate-200">
            <div className="text-slate-400">// Example</div>
            <div className="mt-2">Selling price = <span className="text-emerald-400">₹999</span></div>
            <div>Product cost = ₹400</div>
            <div>Shipping = ₹60 · Fees = ₹120 · Ads = ₹70</div>
            <div className="mt-2 border-t border-white/10 pt-2">Total cost = ₹650</div>
            <div>Net profit = <span className="text-emerald-400">₹349</span></div>
            <div>Profit margin = <span className="text-emerald-400">34.93%</span></div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 pb-20 sm:px-6">
        <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">FAQ</h2>
        <Accordion type="single" collapsible className="mt-6 w-full">
          {HOME_FAQ.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
              <AccordionContent>{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
